import type { SxProps, Theme } from "@mui/material";
import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { CommunicationImageSource } from "../investorCommunicationDetailsTypes";

interface ImageProps {
  sx?: SxProps<Theme>;
  fallback?: ReactNode;
  placeholder?: ReactNode;
}

interface HeroImageElementProps extends Pick<ImageProps, "sx" | "placeholder"> {
  src: string;
  onError: () => void;
}

// Keep the placeholder until the image has decoded, otherwise the img renders with zero height and the layout jumps.
const HeroImageElement = ({ src, sx, placeholder, onError }: HeroImageElementProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const imageSx = useMergedSx(sx, isLoaded ? undefined : { display: "none" });

  return (
    <>
      {!isLoaded && placeholder}
      <Box component="img" src={src} alt="" sx={imageSx} onLoad={() => setIsLoaded(true)} onError={onError} />
    </>
  );
};

const LocalHeroImage = ({ url, sx, fallback, placeholder }: ImageProps & { url: string }) => {
  const [isLoadFailed, setIsLoadFailed] = useState(false);

  if (isLoadFailed) {
    return fallback ?? null;
  }

  return <HeroImageElement src={url} sx={sx} placeholder={placeholder} onError={() => setIsLoadFailed(true)} />;
};

// The download URL is a short-lived SAS link, so it is fetched per mount and refreshed once on load failure.
const SavedHeroImage = ({ communicationId, sx, fallback, placeholder }: ImageProps & { communicationId: string }) => {
  const [hasRetried, setHasRetried] = useState(false);
  const [isLoadFailed, setIsLoadFailed] = useState(false);

  const getDownloadInfo = useCallback(
    () => adminApi.getInvestorCommunicationHeroImageDownloadInfo(communicationId),
    [communicationId]
  );

  const [downloadInfo, fetchError, { fetch: refetchDownloadInfo }] = useFetch(getDownloadInfo);

  const handleImageError = () => {
    if (hasRetried) {
      logError("Hero image failed to load after URL refresh", "[InvestorCommunicationHeroImage] onError");
      setIsLoadFailed(true);
      return;
    }

    setHasRetried(true);
    refetchDownloadInfo();
  };

  if (fetchError) {
    logError(fetchError, "[InvestorCommunicationHeroImage] getInvestorCommunicationHeroImageDownloadInfo");
  }

  if (fetchError || isLoadFailed) {
    return fallback ?? null;
  }

  if (downloadInfo === undefined) {
    return placeholder ?? null;
  }

  return (
    <HeroImageElement
      key={downloadInfo.downloadUrl}
      src={downloadInfo.downloadUrl}
      sx={sx}
      placeholder={placeholder}
      onError={handleImageError}
    />
  );
};

interface Props extends ImageProps {
  source: CommunicationImageSource;
}

const InvestorCommunicationHeroImage = ({ source, ...imageProps }: Props) =>
  source.kind === "saved" ? (
    <SavedHeroImage communicationId={source.communicationId} {...imageProps} />
  ) : (
    <LocalHeroImage url={source.url} {...imageProps} />
  );

export default InvestorCommunicationHeroImage;
